import React, {useMemo} from 'react';
import type {ButtonProps} from '@mui/material';
import {useTheme} from '@mui/material';
import {assert} from 'assert-ts';
import type {IconName} from 'components/icons/types';
import type {ThesaurusNode} from 'services/thesaurus/types';
import {useLocalization} from 'localization';
import {ActionButtonV2} from 'components/button/ActionButtonV2';
import {Card} from 'components/card';
import {Layout} from 'components/layout';
import {Spacer} from 'components/spacer';
import {ThesaurusNodeBreadcrumb} from 'components/thesaurus';
import {useThesaurusEditStateContext} from '../contexts';
import {GrepDetailsBody} from './GrepDetailsBody';
import {ThesaurusNodeDetailsBody} from './ThesaurusNodeDetailsBody';
import {ThesaurusNodeDetailsTitle} from './ThesaurusNodeDetailsTitle';

type Props = {
  node: ThesaurusNode;
};

export const ThesaurusNodeDetails: React.FC<Props> = ({node}) => {
  const {tLoose} = useLocalization();
  const {colors} = useTheme();
  const {
    thesaurus,
    focusNodeId,
    gotoFocusNodeId,
    focusNodeAction,
    addNodeId,
    removeNodeId,
    categoryId,
    includeDeactivatedValues,
  } = useThesaurusEditStateContext();

  const {nodeId, actionTitle, actionIcon, actionColor, onAction} =
    useMemo(() => {
      const nodeId = assert(
        focusNodeId,
        'ThesaurusNodeDetails: focusNodeId expected',
      );

      const actionType = focusNodeAction?.type ?? 'select';
      const actionTitle = tLoose(`extension.thesaurusEdit.${actionType}`);
      const actionIcon: IconName =
        actionType === 'select'
          ? 'Check'
          : actionType === 'swap'
            ? 'Swap'
            : 'Remove';
      const actionColor: ButtonProps['color'] =
        actionType === 'select' ? 'primary' : 'secondary';

      return {
        nodeId,
        actionTitle,
        actionIcon,
        actionColor,
        onAction: () =>
          actionType === 'remove' ? removeNodeId(nodeId) : addNodeId(nodeId),
      };
    }, [addNodeId, focusNodeAction, focusNodeId, removeNodeId, tLoose]);

  // Not allowed to add a category, or when no action
  const hasAction =
    (includeDeactivatedValues || node.selectable) &&
    categoryId !== focusNodeId &&
    focusNodeAction;

  return (
    <Card colorx={colors.offWhite}>
      <Card.Header>
        <Layout
          horizontal
          flex={1}
          overflow="hidden"
          alignItems="center"
          justifyContent={'space-between'}>
          <ThesaurusNodeBreadcrumb
            nodeId={nodeId}
            thesaurus={thesaurus}
            onClick={gotoFocusNodeId}
          />
          {hasAction ? (
            <Layout horizontal>
              <Spacer size={2} />
              <ActionButtonV2
                title={actionTitle}
                icon={actionIcon}
                color={actionColor}
                showSpinnerOnClick
                onClick={onAction}
              />
            </Layout>
          ) : null}
        </Layout>
      </Card.Header>
      <Card.Content>
        <Layout>
          <ThesaurusNodeDetailsTitle node={node} />
          {thesaurus.id === 'grep' ? (
            <>
              <Spacer size={1} />
              <GrepDetailsBody node={node} />
            </>
          ) : (
            <ThesaurusNodeDetailsBody node={node} />
          )}
        </Layout>
      </Card.Content>
    </Card>
  );
};
